import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link2, Unlink, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtWhen } from "@/components/TenantTimeline";

const RESULT_MSG = {
  connected: ["success", "Xero connected"],
  cancelled: ["error", "Xero connection was cancelled"],
  expired: ["error", "The Xero sign-in took too long. Please try again."],
  failed: ["error", "Couldn't connect to Xero. Please try again."],
  "one-org": ["error", "Select only the King Country Self Storage organisation when connecting."],
};

export const XeroPanel = () => {
  const [s, setS] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.adminXeroStatus().then((d) => { setS(d); setCfg(d.settings); }).catch(() => toast.error("Could not load Xero status")), []);
  useEffect(() => {
    load();
    const r = params.get("xero");
    if (r && RESULT_MSG[r]) {
      const [kind, msg] = RESULT_MSG[r];
      toast[kind](msg);
      params.delete("xero");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Xero's sign-in breaks inside an embedded frame, so use a top-level tab when framed
  const connect = async () => {
    const framed = window.self !== window.top;
    const tab = framed ? window.open("", "_blank") : null;
    try {
      const { url } = await api.adminXeroConnect();
      if (tab) { tab.location.href = url; toast.info("Finish connecting Xero in the new tab, then come back and refresh."); }
      else if (framed) { toast.error("Allow pop-ups for this site, or open the app in its own tab, then press Connect again."); }
      else { window.location.href = url; }
    } catch (e) {
      tab?.close();
      toast.error(e.response?.data?.detail || "Could not start Xero connection");
    }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Xero? Overdue checks will stop until it's reconnected.")) return;
    await api.adminXeroDisconnect().catch(() => {});
    toast.success("Xero disconnected"); load();
  };
  const check = async () => {
    setBusy(true);
    try { await api.overdueCheckNow(); toast.success("Checked Xero"); } catch (e) { toast.error(e.response?.data?.detail || "Xero check failed"); } finally { setBusy(false); load(); }
  };
  useEffect(() => {
    const onFocus = () => { api.adminXeroStatus().then(setS).catch(() => {}); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const d = await api.adminUpdateOverdueSettings({ overdue_days: Number(cfg.overdue_days), recent_contact_days: Number(cfg.recent_contact_days), copy_template: cfg.copy_template });
      setCfg(d); toast.success("Overdue settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };

  if (!s || !cfg) return null;
  return (
    <div className="space-y-4" data-testid="xero-panel">
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
        <h3 className="font-display font-bold text-slate-900">Xero connection (read-only)</h3>
        {!s.configured ? (
          <p className="text-sm text-amber-700 flex gap-2" data-testid="xero-not-configured"><AlertTriangle className="h-4 w-4 mt-0.5" /> The Xero connection codes haven't been added yet.</p>
        ) : s.connected ? (
          <p className="text-sm text-green-700 flex gap-2" data-testid="xero-connected"><CheckCircle2 className="h-4 w-4 mt-0.5" /> Connected to {s.org_name || "Xero"}{s.connected_at ? ` since ${fmtWhen(s.connected_at)}` : ""}</p>
        ) : <p className="text-sm text-slate-600" data-testid="xero-disconnected">Not connected.</p>}
        {s.needs_reconnect && <p className="text-sm text-amber-700" data-testid="xero-needs-reconnect">Xero needs reconnecting — press Connect to Xero again.</p>}
        <div className="text-xs text-slate-500 space-y-0.5">
          <p data-testid="xero-last-success">Last successful check: {s.last_success ? fmtWhen(s.last_success) : "never"}</p>
          {s.last_error && s.connected && <p className="text-red-600" data-testid="xero-last-error">Last error: {s.last_error}</p>}
          <p>Redirect URI to register in your Xero app: <code className="bg-slate-100 px-1 rounded">{s.redirect_uri}</code></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={connect} disabled={!s.configured} className="gap-1 bg-[#13B5EA] hover:bg-[#0f9fcf]" data-testid="xero-connect-btn"><Link2 className="h-4 w-4" /> {s.connected ? "Reconnect Xero" : "Connect to Xero"}</Button>
          {s.connected && <Button variant="outline" onClick={check} disabled={busy} className="gap-1" data-testid="xero-check-now"><RefreshCw className="h-4 w-4" /> Check now</Button>}
          {s.connected && <Button variant="outline" onClick={disconnect} className="gap-1 text-red-600" data-testid="xero-disconnect-btn"><Unlink className="h-4 w-4" /> Disconnect</Button>}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
        <h3 className="font-display font-bold text-slate-900">Overdue alerts</h3>
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="space-y-1"><Label>Flag when overdue by more than (days)</Label><Input type="number" min="0" data-testid="overdue-days-input" value={cfg.overdue_days} onChange={(e) => setCfg({ ...cfg, overdue_days: e.target.value })} /></div>
          <div className="space-y-1"><Label>"Recently contacted" period (days)</Label><Input type="number" min="0" data-testid="recent-days-input" value={cfg.recent_contact_days} onChange={(e) => setCfg({ ...cfg, recent_contact_days: e.target.value })} /></div>
        </div>
        <div className="space-y-1">
          <Label>Copy message template</Label>
          <Textarea rows={4} data-testid="copy-template-input" value={cfg.copy_template} onChange={(e) => setCfg({ ...cfg, copy_template: e.target.value })} />
          <p className="text-xs text-slate-500">Tags: [Client Name], [Shed], [Amount Owing], [Weeks Owing], [Contact Name], [Contact Phone]</p>
        </div>
        <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="overdue-settings-save">Save</Button>
      </div>
    </div>
  );
};
